/**
 * AppGrantPicker — multi-app picker used by the internal invite drawer.
 * Each row: checkbox + app label + access_role select + duration controls.
 *
 * Emits an array of { app_key, access_role, expires_at }.
 */
import React, { useState, useEffect } from 'react';

function thirtyDaysFromNow() {
  const d = new Date(); d.setDate(d.getDate() + 30); return d.toISOString().slice(0, 10);
}

export default function AppGrantPicker({ apps, grants, onChange, defaultAccessRole = 'participant' }) {
  // Internal draft mirrors grants[] but keyed by app_key for UI ergonomics.
  const [rows, setRows] = useState(() => {
    const m = {};
    for (const a of (apps || [])) {
      const existing = (grants || []).find(g => g.app_key === a.key);
      m[a.key] = {
        enabled:     !!existing,
        access_role: existing?.access_role || defaultAccessRole,
        durationMode: existing?.expires_at ? 'temporary' : 'permanent',
        expires_at:  existing?.expires_at ? existing.expires_at.slice(0, 10) : thirtyDaysFromNow(),
      };
    }
    return m;
  });

  useEffect(() => {
    const out = [];
    for (const a of (apps || [])) {
      const r = rows[a.key]; if (!r?.enabled) continue;
      out.push({
        app_key:     a.key,
        access_role: r.access_role,
        expires_at:  r.durationMode === 'permanent' ? null : (r.expires_at + 'T23:59:59Z'),
      });
    }
    if (typeof onChange === 'function') onChange(out);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, apps]);

  const update = (key, patch) => setRows(prev => ({ ...prev, [key]: { ...prev[key], ...patch } }));

  return (
    <div style={{ border: '1px solid #e2e8f0', borderRadius: 6 }}>
      {(apps || []).map(a => {
        const r = rows[a.key] || {};
        return (
          <div key={a.key} style={{ padding: '10px 12px', borderTop: '1px solid #f1f5f9' }}>
            <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start', cursor: 'pointer' }}>
              <input type="checkbox" checked={!!r.enabled} onChange={(e) => update(a.key, { enabled: e.target.checked })} style={{ marginTop: 3 }} />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, color: '#0f172a' }}>{a.label}</div>
                {r.enabled && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 6, fontSize: 12 }}>
                    <select value={r.access_role} onChange={(e) => update(a.key, { access_role: e.target.value })}
                      style={{ padding: '4px 8px', fontSize: 12, border: '1px solid #cbd5e1', borderRadius: 6, background: '#ffffff' }}>
                      <option value="participant">Participant</option>
                      <option value="viewer">Viewer (read-only)</option>
                    </select>
                    <label style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                      <input type="radio" checked={r.durationMode === 'permanent'} onChange={() => update(a.key, { durationMode: 'permanent' })} />
                      Permanent
                    </label>
                    <label style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                      <input type="radio" checked={r.durationMode === 'temporary'} onChange={() => update(a.key, { durationMode: 'temporary' })} />
                      Until
                    </label>
                    {r.durationMode === 'temporary' && (
                      <input type="date" value={r.expires_at} onChange={(e) => update(a.key, { expires_at: e.target.value })}
                        style={{ padding: '4px 8px', fontSize: 12, border: '1px solid #cbd5e1', borderRadius: 6 }} />
                    )}
                  </div>
                )}
              </div>
            </label>
          </div>
        );
      })}
    </div>
  );
}
